import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ClickAwayListener, IconButton, Paper, Portal, Typography } from '@mui/material';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { TOGGLE_SHORTCUTS_EVENT } from '../utils/desktopShortcuts';

const SECTION_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'rgba(255, 255, 255, 0.45)',
  mb: 1,
};

// Tous les raccourcis reellement actifs sur navigateur de bureau, y compris
// ceux qui vivent dans le manche, la lecon et le lecteur de backing.
const SECTIONS = [
  {
    title: 'Play',
    rows: [
      { keys: ['Space'], label: 'Play / stop' },
      { keys: ['1', '7'], separator: '–', label: 'Play a chord' },
      { keys: ['←', '→'], label: 'Key down / up' },
      { keys: ['R'], label: 'Key and scale' },
      { keys: ['C', 'X'], separator: '/', label: 'Next / previous jam scale' },
      { keys: ['S'], label: 'Safe / All' },
      { keys: ['T'], label: 'Drone / triad / 7th' },
      { keys: ['M'], label: 'Metronome' },
      { keys: ['L'], label: 'Lock the neck' },
      { keys: ['D'], label: 'Surprise me' },
    ],
  },
  {
    title: 'Lesson',
    rows: [
      { keys: ['←', '→'], label: 'Previous / next step' },
      { keys: ['↑', '↓'], label: 'Previous / next section' },
      { keys: ['Space'], label: 'Play / pause' },
    ],
  },
  {
    title: 'Jam track',
    rows: [
      { keys: ['Space'], label: 'Play / pause' },
      { keys: ['C', 'X'], separator: '/', label: 'Next / previous scale' },
    ],
  },
  {
    title: 'Navigation',
    rows: [
      { keys: ['H'], label: 'Play' },
      { keys: ['J'], label: 'Practice' },
      { keys: ['Y'], label: 'You' },
      { keys: ['V'], label: 'Mixer' },
      { keys: ['?'], label: 'Shortcuts' },
      { keys: ['Esc'], label: 'Close' },
    ],
  },
];

const Keycap = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 24,
      padding: '2px 8px',
      borderRadius: '6px',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: '0.8rem',
      lineHeight: 1.4,
      color: 'rgba(255, 255, 255, 0.9)',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

export default function ShortcutsHelp() {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const openRef = useRef(false);
  openRef.current = open;

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  useEffect(() => {
    const onToggle = () => {
      if (openRef.current) close();
      else setOpen(true);
    };
    window.addEventListener(TOGGLE_SHORTCUTS_EVENT, onToggle);
    return () => window.removeEventListener(TOGGLE_SHORTCUTS_EVENT, onToggle);
  }, []);

  // Echap ferme la popup meme quand le focus est retourne a la page
  useEffect(() => {
    if (!open) return;
    const onKeyDown = event => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const buttonBg = open ? 'rgba(var(--accent-rgb), 0.18)' : 'var(--surface-2)';

  const panel = (
    <Paper
      elevation={8}
      id="shortcuts-help"
      role="region"
      aria-label="Keyboard shortcuts"
      onKeyDown={event => {
        // Meme touche pour ouvrir et fermer : quand le focus est dans le
        // panneau, le raccourci global ne le voit pas passer.
        if (event.key === 'Escape' || event.key === '?') {
          event.stopPropagation();
          close();
        }
      }}
      sx={{
        width: 420,
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'calc(100dvh - 48px)',
        overflowY: 'auto',
        p: 2,
        boxSizing: 'border-box',
        bgcolor: '#15151A',
        backgroundImage: 'none',
        pointerEvents: 'auto',
        borderRadius: '14px',
        color: 'white',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography component="h2" sx={{ ...SECTION_LABEL_SX, mb: 0 }}>Keyboard shortcuts</Typography>
        <IconButton autoFocus size="small" aria-label="Close keyboard shortcuts" onClick={close}
          sx={{ mr: -0.5, color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.1)' } }}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      {SECTIONS.map(({ title, rows }) => (
        <Box key={title} sx={{ mt: 2 }}>
          <Typography component="h3" sx={SECTION_LABEL_SX}>{title}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {rows.map(({ keys, separator, label }) => (
              <Box key={`${title}-${label}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {keys.map((key, index) => (
                    <React.Fragment key={key}>
                      {index > 0 && separator && (
                        <Box component="span" sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.4)' }}>{separator}</Box>
                      )}
                      <Keycap>{key}</Keycap>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Paper>
  );

  return (
    <>
      <Button
        ref={buttonRef}
        aria-label="Keyboard shortcuts"
        aria-expanded={open}
        aria-controls={open ? 'shortcuts-help' : undefined}
        onClick={() => (open ? close() : setOpen(true))}
        sx={{
          pointerEvents: 'auto', minWidth: 55, width: 55, height: 55, p: '8px',
          borderRadius: '28px', textTransform: 'none',
          color: open ? 'var(--accent)' : 'var(--text-primary, #eee)',
          bgcolor: buttonBg,
          '&:hover': { bgcolor: buttonBg },
        }}
      >
        <KeyboardOutlinedIcon sx={{ fontSize: 23 }} />
      </Button>
      {open && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown"
            onClickAway={event => { if (buttonRef.current?.contains(event.target)) return; close(); }}>
            <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', pointerEvents: 'none', zIndex: 1450 }}>
              {panel}
            </Box>
          </ClickAwayListener>
        </Portal>
      )}
    </>
  );
}
